// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import { getQuickViewProductState } from "@bg/App/Store/Selectors/ProductQuickView/QuickViewSelector";
import LocationDetailsPrimaryImage from "@bg/App/Widgets/LocationdetailsImages/LocationDetailsPrimaryImage";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel, ImageModel } from "@insite/client-framework/Types/ApiModels";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Icon from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useEmblaCarousel from "embla-carousel-react";
import React, { FC } from "react";
import { css } from "styled-components";

// eslint-disable-next-line
import SwiperCore, { A11y, Navigation, Pagination, Scrollbar } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";

const enum fields {
    images = "images",
}

interface OwnProps {
    product: CartLineModel | ProductContextModel;
    showQuickView?: boolean;
    fields: {
        [fields.images]: any[];
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    isLoading: getQuickViewProductState(state).isLoading,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface LocationDetailsImagesStyles {
    carouselWrapper?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    fullPrimaryImageModal?: ModalPresentationProps;
}

export const locationDetailsImagesStyles: LocationDetailsImagesStyles = {
    carouselWrapper: {
        css: css`
            @media print {
                display: none;
            }
            .swiper-container {
                @media (max-width: 760px) {
                    height: 88px !important;
                    max-width: 300px;
                }
            }
            width: 88px;
            margin-top: 20px !important;
            margin-right: 28px !important;
            position: relative;
            max-height: 412px;
            .swiper-carousel {
                height: 412px;
                overflow: hidden;
                width: 88px;
                padding-top: 5px;

                @media (max-width: 760px) {
                    width: 100%;
                    padding-top: 0;
                }
            }

            .swiper-button-prev-pdp-small,
            .swiper-button-next-pdp-small {
                display: none;
            }

            @media (max-width: 760px) {
                height: 88px;
                order: 2;
                width: 100%;
                padding-top: 0;
                .swiper-button-prev-pdp-small,
                .swiper-button-next-pdp-small {
                    color: ${getColor("text.main")};
                    position: absolute;
                    display: block;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 2;
                    &.hide-navigation {
                        display: none;
                    }
                }

                .swiper-button-prev-pdp-small {
                    left: 10px;
                }

                .swiper-button-next-pdp-small {
                    right: 10px;
                }
            }

            .swiper-button-disabled {
                display: none;
            }

            .swiper-button-prev-pdp,
            .swiper-button-next-pdp {
                color: ${getColor("text.main")};
                position: absolute;
                left: 50%;
                transform: translate(-50%);
                z-index: 2;

                @media (max-width: 760px) {
                    display: none;
                }

                &.hide-navigation {
                    display: none;
                }
            }

            .swiper-button-prev-pdp {
                top: -25px;
            }
            .swiper-button-next-pdp {
                bottom: -25px;
            }
            .swiper-slide {
                height: 88px !important;
                @media (max-width: 760px) {
                    display: flex;
                    justify-content: center;
                }
            }
        `,
    },
    image: {
        css: css``,
    },
    selectedImage: {
        css: css`
            border: 3px solid ${getColor("text.main")};
        `,
    },
};

const styles = locationDetailsImagesStyles;

const LocationDetailsImages = ({ fields }: Props) => {
    const [emblaRef, embla] = useEmblaCarousel({
        align: "start",
        loop: true,
    });
    const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = React.useState(false);

    const imageClickHandler = (index: number) => {
        setSelectedImageIndex(index);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const fullPrimaryImageModalOpenHandler = () => {
        setFullImageModalIsOpen(true);
    };

    if (fields.images.length === 0) {
        return null;
    }
    const images: ImageModel[] = [];
    fields.images.forEach((item, index) => {
        images.push({
            mediumImagePath: item.fields.mediumImagePath,
            smallImagePath: item.fields.smallImagePath,
            largeImagePath: item.fields.largeImagePath,
            imageType: "Static",
            sortOrder: 1,
            imageAltText: item.fields.altText,
            id: `${index}`,
            name: `location-image-${index}`,
        });
    });

    const locationsImagesCarousel = () => (
        <StyledWrapper {...styles.carouselWrapper}>
            <Swiper
                navigation={{
                    nextEl: ".swiper-button-next-pdp",
                    prevEl: ".swiper-button-prev-pdp",
                }}
                direction="vertical"
                className="swiper-carousel"
                spaceBetween={15}
                loop={false}
                slidesPerView={4}
                breakpoints={{
                    0: {
                        slidesPerView: 1,
                        direction: "horizontal",
                        navigation: {
                            prevEl: ".swiper-button-prev-pdp-small",
                            nextEl: ".swiper-button-next-pdp-small",
                        },
                    },
                    761: {
                        slidesPerView: 4,
                    },
                }}
            >
                {images &&
                    images.map((item, index) => (
                        <SwiperSlide
                            key={`locationDetailsImage_${item.smallImagePath || item.mediumImagePath}`}
                            className="swiper-slide"
                        >
                            <StyledWrapper>
                                <StyledWrapper>
                                    <LazyImage
                                        altText={item.imageAltText}
                                        {...(index === selectedImageIndex ? styles.selectedImage : styles.image)}
                                        height="88px"
                                        cloudinary={true}
                                        src={item.smallImagePath || item.mediumImagePath}
                                        onClick={() => imageClickHandler(index)}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        </SwiperSlide>
                    ))}
            </Swiper>
            <div className={`swiper-button-prev-pdp ${images.length < 4 ? "hide-navigation" : ""}`}>
                <Icon src="ChevronUp" size={20} color="tertiary" />
            </div>
            <div className={`swiper-button-next-pdp ${images.length < 4 ? "hide-navigation" : ""}`}>
                <Icon src="ChevronDown" size={20} color="tertiary" />
            </div>
            <div className={`swiper-button-prev-pdp-small ${images.length < 2 ? "hide-navigation" : ""}`}>
                <Icon src="ChevronLeft" size={20} color="tertiary" />
            </div>
            <div className={`swiper-button-next-pdp-small ${images.length < 2 ? "hide-navigation" : ""}`}>
                <Icon src="ChevronRight" size={20} color="tertiary" />
            </div>
        </StyledWrapper>
    );

    return (
        <>
            <StyledWrapper>
                <StyledWrapper
                    css={css`
                        display: flex;
                        padding-top: 25px;
                        flex-dirction: ${images.length > 2 ? "column" : "row"};
                        @media (max-width: 760px) {
                            flex-wrap: wrap;
                        }
                    `}
                >
                    {locationsImagesCarousel()}
                    <StyledWrapper
                        css={css`
                            position: relative;
                            flex-grow: 1;
                            display: flex;
                            justfiy-content: center;
                            align-items: center;
                        `}
                    >
                        <LocationDetailsPrimaryImage
                            image={images[selectedImageIndex]}
                            useLargeImage={true}
                            onClick={fullPrimaryImageModalOpenHandler}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <StyledWrapper
                    css={css`
                        display: flex;
                        padding-top: 25px;
                        flex-dirction: ${images.length > 2 ? "column" : "row"};
                        @media (max-width: 760px) {
                            flex-wrap: wrap;
                        }
                    `}
                >
                    {locationsImagesCarousel()}
                    <LocationDetailsPrimaryImage image={images[selectedImageIndex]} useLargeImage={true} />
                </StyledWrapper>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: LocationDetailsImages,
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.images,
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                getDisplay: (item: HasFields) => {
                    return item.fields.largeImagePath;
                },
                fieldDefinitions: [
                    {
                        name: "largeImagePath",
                        displayName: "Large Image Path",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },

                    {
                        name: "mediumImagePath",
                        displayName: "Medium Image Path",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "smallImagePath",
                        displayName: "Small Image Path",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "altText",
                        displayName: "Alt Text *",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
